import React from 'react';
import { PieChart, BarChart2, Lightbulb } from 'lucide-react';

/**
 * Секция распределения бюджета
 */
const DistributionSection = ({ 
  formData,
  handleInputChange,
  errors
}) => {
  // Расчет оставшегося процента
  const totalPercentage = 
    parseFloat(formData.distribution.savingsPercentage || 0) + 
    parseFloat(formData.distribution.entertainmentPercentage || 0) + 
    parseFloat(formData.distribution.essentialsPercentage || 0) + 
    parseFloat(formData.distribution.investmentsPercentage || 0);
  
  // Проверка на 100%
  const isValidTotal = Math.abs(totalPercentage - 100) < 0.1;

  return (
    <div className="mb-6 bg-white dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
        Распределение бюджета
      </h3>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Укажите желаемое процентное распределение вашего дохода (в сумме должно быть 100%)
        </p>
      </div>

      <div className="flex flex-col space-y-4">
        {/* Обязательные расходы */}
        <div>
          <div className="flex justify-between mb-1">
            <label htmlFor="essentialsPercentage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Обязательные расходы (жилье, еда, транспорт)
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formData.distribution.essentialsPercentage}%
            </span>
          </div>
          <input
            type="range"
            id="essentialsPercentage"
            name="distribution.essentialsPercentage"
            min="0"
            max="100"
            step="1"
            value={formData.distribution.essentialsPercentage}
            onChange={handleInputChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-600"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Развлечения */}
        <div>
          <div className="flex justify-between mb-1">
            <label htmlFor="entertainmentPercentage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Развлечения и необязательные расходы
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formData.distribution.entertainmentPercentage}%
            </span>
          </div>
          <input
            type="range"
            id="entertainmentPercentage"
            name="distribution.entertainmentPercentage"
            min="0"
            max="100"
            step="1"
            value={formData.distribution.entertainmentPercentage}
            onChange={handleInputChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Сбережения */}
        <div>
          <div className="flex justify-between mb-1">
            <label htmlFor="savingsPercentage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Сбережения (финансовые цели и НЗ)
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formData.distribution.savingsPercentage}%
            </span>
          </div>
          <input
            type="range"
            id="savingsPercentage"
            name="distribution.savingsPercentage"
            min="0"
            max="100"
            step="1"
            value={formData.distribution.savingsPercentage}
            onChange={handleInputChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Инвестиции */}
        <div>
          <div className="flex justify-between mb-1">
            <label htmlFor="investmentsPercentage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Инвестиции (долгосрочный рост капитала)
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formData.distribution.investmentsPercentage}%
            </span>
          </div>
          <input
            type="range"
            id="investmentsPercentage"
            name="distribution.investmentsPercentage"
            min="0"
            max="100"
            step="1"
            value={formData.distribution.investmentsPercentage}
            onChange={handleInputChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Итоговая сумма процентов */}
        <div className={`mt-4 p-3 rounded-lg flex items-center justify-between ${
          isValidTotal 
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
        }`}>
          <span className="font-medium">Общая сумма:</span>
          <span className="font-bold">{totalPercentage}%</span>
        </div>

        {errors.distribution && (
          <p className="mt-1 text-xs text-red-500">{errors.distribution}</p>
        )}
      </div>

      {/* Визуализация распределения */}
      <div className="mt-6 relative h-12 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
        <div 
          className="absolute top-0 left-0 h-full bg-amber-500 dark:bg-amber-600" 
          style={{ width: `${formData.distribution.essentialsPercentage}%` }}
          title={`Обязательные расходы: ${formData.distribution.essentialsPercentage}%`}
        ></div>
        <div 
          className="absolute top-0 h-full bg-red-500 dark:bg-red-600" 
          style={{ 
            left: `${formData.distribution.essentialsPercentage}%`,
            width: `${formData.distribution.entertainmentPercentage}%` 
          }}
          title={`Развлечения: ${formData.distribution.entertainmentPercentage}%`}
        ></div>
        <div 
          className="absolute top-0 h-full bg-blue-500 dark:bg-blue-600" 
          style={{ 
            left: `${parseFloat(formData.distribution.essentialsPercentage) + parseFloat(formData.distribution.entertainmentPercentage)}%`,
            width: `${formData.distribution.savingsPercentage}%` 
          }}
          title={`Сбережения: ${formData.distribution.savingsPercentage}%`}
        ></div>
        <div 
          className="absolute top-0 h-full bg-purple-500 dark:bg-purple-600" 
          style={{ 
            left: `${parseFloat(formData.distribution.essentialsPercentage) + parseFloat(formData.distribution.entertainmentPercentage) + parseFloat(formData.distribution.savingsPercentage)}%`,
            width: `${formData.distribution.investmentsPercentage}%` 
          }}
          title={`Инвестиции: ${formData.distribution.investmentsPercentage}%`}
        ></div>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1 text-center text-xs">
        <div className="text-amber-700 dark:text-amber-300">Обязательные</div>
        <div className="text-red-700 dark:text-red-300">Развлечения</div>
        <div className="text-blue-700 dark:text-blue-300">Сбережения</div>
        <div className="text-purple-700 dark:text-purple-300">Инвестиции</div>
      </div>

      {/* Рекомендации по распределению */}
      <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="flex items-start">
          <BarChart2 className="w-5 h-5 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">
              Популярные модели распределения бюджета
            </h4>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">
              <div className="mt-2">
                <div className="font-medium mb-1">1. Правило 50/30/20</div>
                <ul className="list-disc list-inside pl-1 space-y-0.5">
                  <li>50% – на необходимые расходы</li>
                  <li>30% – на желания и развлечения</li>
                  <li>20% – на сбережения и инвестиции</li>
                </ul>
              </div>
              <div className="mt-3">
                <div className="font-medium mb-1">2. Метод 60/20/20</div>
                <ul className="list-disc list-inside pl-1 space-y-0.5">
                  <li>60% – на необходимые расходы</li>
                  <li>20% – на личные расходы</li>
                  <li>20% – на финансовые цели</li>
                </ul>
              </div>
              <div className="mt-3">
                <div className="font-medium mb-1">3. Метод 70/20/10</div>
                <ul className="list-disc list-inside pl-1 space-y-0.5">
                  <li>70% – на ежемесячные расходы</li>
                  <li>20% – на сбережения</li>
                  <li>10% – на инвестиции или благотворительность</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Полезный совет */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 flex items-start">
        <Lightbulb className="w-5 h-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Выбирайте распределение, которое подходит именно вашей ситуации. По мере роста доходов 
          старайтесь увеличивать процент на сбережения и инвестиции, а не на текущие расходы.
        </p>
      </div>
    </div>
  );
};

export default DistributionSection;